import { AxiosError } from 'axios'
import DataTableControllerWithoutCol from 'components/common/dataTable/dataTableControllerWithoutCol'
import { ImportColumn } from 'components/common/importModal'
import ActiveFilterChip from 'components/common/activeFilterChip'
import { getImportTemplate, downloadImportSample } from 'utils/api/importApi'
import PriceLabelDataTable from 'components/priceLabel/priceLabelDataTable'
import PriceLabelFilterForm from 'components/priceLabel/priceLabelFilterForm'
import { FILTER_FIELD_PRICE_LABELS } from 'components/priceLabel/utils'
import { usePageAccess, useQuery } from 'hooks'
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import toast from 'react-hot-toast'
import {
  exportPriceLabelData,
  importPriceLabelData,
} from 'utils/api/priceLableApi'
import { filterEmptyValues } from 'utils/helpers'

const PriceLabelListPage = () => {
  const { isEditor } = usePageAccess()
  const [showFilter, setShowFilter] = useState(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [importColumns, setImportColumns] = useState<ImportColumn[]>([])

  const { getQuery } = useQuery()

  useEffect(() => {
    getImportTemplate('priceLabel')
      .then((res: { data: { columns: ImportColumn[] } }) =>
        setImportColumns(res.data.columns),
      )
      .catch(() => {})
  }, [])

  const handleDownloadSample = async () => {
    const response = await downloadImportSample('priceLabel')
    const url = URL.createObjectURL(
      new Blob([response.data], { type: 'text/csv' }),
    )
    const link = document.createElement('a')
    link.href = url
    link.download = 'price_label_sample.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  const toggleFilterForm = () => {
    setShowFilter(!showFilter)
  }

  const handleExport = async () => {
    const filters = getQuery(FILTER_FIELD_PRICE_LABELS)

    // if not filters found in URL
    if (!filters) {
      return toast.error('Filters are required to export data!', {
        duration: 3000,
      })
    }

    // filtering empty values and keys of filter
    const masterFilter = filterEmptyValues(filters)

    // if filters are present with empty values
    if (Object.keys(masterFilter).length <= 0) {
      return toast.error('Please select at least on filter to export data!', {
        duration: 3000,
      })
    }

    const loadingToast = toast.loading('Loading...', { duration: Infinity })

    try {
      await exportPriceLabelData({ filters: masterFilter })
      toast.success('File will be sent to you in email!', {
        id: loadingToast,
        duration: 3000,
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data?.errors?.msg?.runningService) {
          return toast.error(error.response.data.errors.msg.runningService, {
            id: loadingToast,
            duration: 3000,
          })
        }
      }
      toast.error('Failed to export Price Label Data.', {
        id: loadingToast,
        duration: 3000,
      })
    }
  }

  return (
    <>
      <Helmet>
        <title>GAG Aggregator | Price Label</title>
      </Helmet>

      <div className="mb-2 flex flex-wrap items-center justify-between md:mb-0 md:flex-nowrap">
        <h3 className="title">Price Labels</h3>
      </div>

      <DataTableControllerWithoutCol
        isEditor={isEditor}
        handleFilter={toggleFilterForm}
        showFilter={showFilter}
        handleImport={importPriceLabelData}
        placeholder="Search by Barcode, Store Code"
        handleExport={handleExport}
        loading={loading}
        importColumns={importColumns}
        importModuleName="price label"
        onDownloadSample={handleDownloadSample}
      />

      {!showFilter && (
        <ActiveFilterChip filterKey={FILTER_FIELD_PRICE_LABELS} />
      )}

      {showFilter ? (
        <PriceLabelFilterForm toggleFilterForm={toggleFilterForm} />
      ) : (
        <PriceLabelDataTable
          loading={loading}
          setLoading={setLoading}
          isEditor={isEditor}
        />
      )}
    </>
  )
}

export default PriceLabelListPage
